import { useEffect, useMemo, useState } from 'react';
import { Navigate } from 'react-router-dom';

import { AppSelect } from '@/components/AppSelect';
import { SparkIcon, VisitorIcon } from '@/components/Icons';
import { PhoneNumberField } from '@/components/PhoneNumberField';
import { StatMetric } from '@/components/StatMetric';
import { useServiceCatalogState } from '@/context/ServiceContext';
import { useSessionState } from '@/context/SessionContext';
import { useStaffDirectoryState } from '@/context/StaffContext';
import { useVisitorState } from '@/context/VisitorContext';
import { fetchClients } from '@/lib/api';
import { buildCustomerSummaries, normalizeCustomerKey } from '@/lib/customers';
import { formatInrCurrency, getInitials } from '@/lib/format';
import { tintStyle } from '@/lib/style';
import {
  DEFAULT_PHONE_COUNTRY_CODE,
  joinPhoneNumber,
  splitPhoneNumber,
  type PhoneCountryCode,
} from '@/lib/phone';
import type { ClientRecord } from '@/lib/types';
import { visitorFormSchema, flattenFieldErrors } from '@/lib/validation';

type LookupEntry = {
  key: string;
  name: string;
  phone: string;
  visits: number;
  source: 'local' | 'api';
  services?: string[];
  visitFrequency?: string | null;
  amount?: number;
};

function normalizeLookupText(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

function ServiceChip({
  label,
  selected,
  onClick,
}: {
  label: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button className={`chip${selected ? ' active' : ''}`} type="button" onClick={onClick}>
      {label}
    </button>
  );
}

function LookupSuggestion({
  name,
  phone,
  visits,
  onClick,
}: {
  name: string;
  phone: string;
  visits: number;
  onClick: () => void;
}) {
  return (
    <button className="list-item interactive" type="button" onClick={onClick}>
      <div className="media-row">
        <div className="avatar">{getInitials(name)}</div>
        <div>
          <strong>
            {name} · {phone}
          </strong>
          <span>{visits} visit{visits === 1 ? '' : 's'} saved</span>
        </div>
      </div>
      <span className="badge soft">Fill</span>
    </button>
  );
}

export function VisitorPage() {
  const { sessionRole, sessionName, sessionStaffId, isAuthenticated } = useSessionState();
  const { isReady: staffReady, staffMembers } = useStaffDirectoryState();
  const { isReady: serviceReady, services } = useServiceCatalogState();
  const { isReady: visitorReady, visitors, registerVisitor } = useVisitorState();
  const [visitorName, setVisitorName] = useState('');
  const [visitorPhone, setVisitorPhone] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState<PhoneCountryCode>(DEFAULT_PHONE_COUNTRY_CODE);
  const [visitFrequency, setVisitFrequency] = useState('');
  const [amountText, setAmountText] = useState('');
  const [selectedServices, setSelectedServices] = useState<string[]>([]);
  const [selectedStaffId, setSelectedStaffId] = useState<string | null>(sessionStaffId ?? null);
  const [searchQuery, setSearchQuery] = useState('');
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiClients, setApiClients] = useState<ClientRecord[]>([]);

  const isEnterprise = sessionRole === 'enterprise';

  useEffect(() => {
    if (sessionRole === 'staff' && sessionStaffId) {
      setSelectedStaffId(sessionStaffId);
      return;
    }

    if (!selectedStaffId && staffMembers.length > 0) {
      setSelectedStaffId(staffMembers[0].id);
    }
  }, [selectedStaffId, sessionRole, sessionStaffId, staffMembers]);

  useEffect(() => {
    if (!isAuthenticated) {
      setApiClients([]);
      return;
    }

    const controller = new AbortController();
    let active = true;

    fetchClients(controller.signal)
      .then((payload) => {
        if (!active) {
          return;
        }

        setApiClients(payload.items ?? []);
      })
      .catch(() => {
        if (!active || controller.signal.aborted) {
          return;
        }

        setApiClients([]);
      });

    return () => {
      active = false;
      controller.abort();
    };
  }, [isAuthenticated]);

  const scopedVisitors = useMemo(
    () => (sessionRole === 'staff' && sessionStaffId ? visitors.filter((visitor) => visitor.staffId === sessionStaffId) : visitors),
    [sessionRole, sessionStaffId, visitors]
  );

  const customerSummaries = useMemo(() => buildCustomerSummaries(scopedVisitors), [scopedVisitors]);

  const lookupEntries = useMemo(() => {
    const localEntries: LookupEntry[] = customerSummaries.map((customer) => ({
      key: customer.key,
      name: customer.name,
      phone: customer.phone,
      visits: customer.totalVisits,
      source: 'local',
      services: customer.latestServices,
      visitFrequency: customer.latestVisit.visitFrequency,
      amount: customer.latestVisit.totalAmount,
    }));

    const localKeys = new Set(localEntries.map((entry) => entry.key));
    const apiEntries: LookupEntry[] = apiClients
      .map((client) => ({
        key: normalizeCustomerKey(client.key ?? client.number ?? client.id),
        name: client.name,
        phone: client.number,
        visits: client.total_visits ?? client.appointment_count ?? 0,
        source: 'api' as const,
        services: client.latest_services,
      }))
      .filter((entry) => !localKeys.has(entry.key));

    return [...localEntries, ...apiEntries];
  }, [apiClients, customerSummaries]);

  const searchResults = useMemo(() => {
    const query = normalizeLookupText(searchQuery);

    if (!query) {
      return lookupEntries.slice(0, 5);
    }

    return lookupEntries
      .filter((entry) => {
        return (
          normalizeLookupText(entry.name).includes(query) ||
          normalizeLookupText(entry.phone).includes(query) ||
          normalizeCustomerKey(entry.phone).includes(query)
        );
      })
      .slice(0, 5);
  }, [lookupEntries, searchQuery]);

  const selectedStaff = useMemo(() => {
    if (!selectedStaffId) {
      return null;
    }

    return staffMembers.find((staff) => staff.id === selectedStaffId) ?? null;
  }, [selectedStaffId, staffMembers]);

  const totalAmount = useMemo(() => {
    return scopedVisitors.reduce((sum, visitor) => sum + visitor.totalAmount, 0);
  }, [scopedVisitors]);

  const serviceCount = services.length;
  const customerCount = Math.max(customerSummaries.length, apiClients.length);
  const visitCount = scopedVisitors.length;
  const hasSummaryData = visitCount > 0 || totalAmount > 0 || serviceCount > 0 || staffMembers.length > 0;
  const recentVisitors = useMemo(() => scopedVisitors.slice(0, 6), [scopedVisitors]);

  const quickServiceOptions = useMemo(() => {
    return services.map((service) => service.name);
  }, [services]);

  const staffSelectOptions = useMemo(
    () =>
      staffMembers.map((staff) => ({
        value: staff.id,
        label: `${staff.name} · ${staff.role}`,
      })),
    [staffMembers]
  );

  async function handleSubmit() {
    if (isSubmitting) {
      return;
    }

    const validation = visitorFormSchema.safeParse({
      visitorName,
      visitorPhone: visitorPhone.replace(/\D/g, ''),
      visitFrequency,
      amountText,
      services: selectedServices,
    });

    if (!validation.success) {
      setFieldErrors(flattenFieldErrors(validation.error.flatten().fieldErrors));
      setFeedback(null);
      return;
    }

    if (!selectedStaffId) {
      setFeedback({ kind: 'error', message: 'Choose a staff member before saving.' });
      return;
    }

    setIsSubmitting(true);
    setFeedback(null);
    setFieldErrors({});

    try {
      const result = await registerVisitor({
        visitorName: validation.data.visitorName,
        visitorPhone: joinPhoneNumber(phoneCountryCode, validation.data.visitorPhone),
        visitFrequency: validation.data.visitFrequency || null,
        services: validation.data.services,
        staffId: selectedStaffId,
        totalAmount: validation.data.amountText,
        createdByRole: isEnterprise ? 'enterprise' : 'staff',
        createdByName: sessionName ?? 'Admin',
      });

      setFeedback({ kind: 'success', message: `${result.visitorName} saved with ${result.services.length} service(s).` });
      setVisitorName('');
      setVisitorPhone('');
      setPhoneCountryCode(DEFAULT_PHONE_COUNTRY_CODE);
      setVisitFrequency('');
      setAmountText('');
      setSelectedServices([]);
      setSearchQuery('');
    } catch (error) {
      setFeedback({
        kind: 'error',
        message: error instanceof Error && error.message ? error.message : 'Could not save the visitor.',
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  function fillFromSuggestion(entry: LookupEntry) {
    setVisitorName(entry.name);
    const parsedPhone = splitPhoneNumber(entry.phone);
    setVisitorPhone(parsedPhone.phoneNumber);
    setPhoneCountryCode(parsedPhone.countryCode);
    setVisitFrequency(entry.visitFrequency ?? '');
    setAmountText(entry.amount ? String(entry.amount) : '');
    setSelectedServices(entry.services ?? []);
    setSearchQuery(entry.name);
  }

  if (!visitorReady || !staffReady || !serviceReady) {
    return <div className="section">Loading visitor workflow...</div>;
  }

  return (
    <div className="section page-grid">
      <section className="hero-layout">
        <div className="hero-copy">
          <span className="eyebrow">
            <VisitorIcon size={14} />
            <strong>Visitor</strong>
            {customerCount > 0 ? <span>{customerCount} customer threads</span> : null}
          </span>

          <h1 className="display-title">
            <span>Register visits</span>
          </h1>

        </div>

        {hasSummaryData ? (
          <div className="stats-grid">
            {visitCount > 0 ? <StatMetric label="Visits" value={String(visitCount)} hint="Saved locally" /> : null}
            {totalAmount > 0 ? (
              <StatMetric label="Amount" value={formatInrCurrency(totalAmount)} hint="Local total" />
            ) : null}
            {serviceCount > 0 ? <StatMetric label="Services" value={String(serviceCount)} hint="From API" /> : null}
            {staffMembers.length > 0 ? (
              <StatMetric label="Team" value={String(staffMembers.length)} hint="From API" />
            ) : null}
          </div>
        ) : null}
      </section>

      <section className="content-grid">
        <div className="stack">
          <div className="panel">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">New visit</h2>
                <p className="panel-caption">Assign services and staff</p>
              </div>
            </div>

            <div className="field-grid">
              <label className="field">
                <span className="field-label">Customer name</span>
                <input
                  className="input"
                  value={visitorName}
                  onChange={(event) => setVisitorName(event.target.value)}
                  placeholder="Visitor name"
                />
                {fieldErrors.visitorName ? <span className="field-error">{fieldErrors.visitorName}</span> : null}
              </label>

              <PhoneNumberField
                countryCode={phoneCountryCode}
                phoneNumber={visitorPhone}
                onChangeCountryCode={setPhoneCountryCode}
                onChangePhoneNumber={setVisitorPhone}
                errorText={fieldErrors.visitorPhone}
              />

              <div className="split-grid">
                <label className="field">
                  <span className="field-label">Visit frequency</span>
                  <input
                    className="input"
                    value={visitFrequency}
                    onChange={(event) => setVisitFrequency(event.target.value)}
                    placeholder="Every 4 weeks"
                  />
                  {fieldErrors.visitFrequency ? <span className="field-error">{fieldErrors.visitFrequency}</span> : null}
                </label>

                <label className="field">
                  <span className="field-label">Total amount</span>
                  <input
                    className="input"
                    value={amountText}
                    onChange={(event) => setAmountText(event.target.value.replace(/\D/g, ''))}
                    placeholder="1799"
                    inputMode="numeric"
                  />
                  {fieldErrors.amountText ? <span className="field-error">{fieldErrors.amountText}</span> : null}
                </label>
              </div>

              <label className="field">
                <span className="field-label">Staff member</span>
                <AppSelect
                  ariaLabel="Staff member"
                  options={staffSelectOptions}
                  value={selectedStaffId}
                  onChange={(nextValue) => setSelectedStaffId(nextValue)}
                  placeholder={staffMembers.length > 0 ? 'Choose staff' : 'No staff available'}
                  isDisabled={!isEnterprise || staffMembers.length === 0}
                />
              </label>

              <div className="field">
                <span className="field-label">Services</span>
                <div className="chip-row">
                  {quickServiceOptions.length > 0 ? (
                    quickServiceOptions.map((service) => (
                      <ServiceChip
                        key={service}
                        label={service}
                        selected={selectedServices.includes(service)}
                        onClick={() =>
                          setSelectedServices((current) =>
                            current.includes(service)
                              ? current.filter((item) => item !== service)
                              : [...current, service]
                          )
                        }
                      />
                    ))
                  ) : (
                    <div className="empty-state">No services loaded from the API yet.</div>
                  )}
                </div>
                {fieldErrors.services ? <span className="field-error">{fieldErrors.services}</span> : null}
              </div>

              {feedback ? <div className={`notice ${feedback.kind}`}>{feedback.message}</div> : null}

              <div className="button-row">
                <button className="primary-button" type="button" onClick={handleSubmit} disabled={isSubmitting}>
                  <SparkIcon size={16} />
                  {isSubmitting ? 'Saving...' : 'Save visit'}
                </button>
              </div>
            </div>
          </div>

          <div className="panel">
            <div className="panel-title-row">
              <div>
                <h2 className="panel-title">Lookup</h2>
              </div>
            </div>

            <label className="field">
              <span className="field-label">Search customers</span>
              <input
                className="input"
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                placeholder="Search existing customers"
              />
            </label>

            {searchResults.length > 0 ? (
              <div className="list form-stack">
                {searchResults.map((entry) => (
                  <LookupSuggestion
                    key={`${entry.source}-${entry.key}`}
                    name={entry.name}
                    phone={entry.phone}
                    visits={entry.visits}
                    onClick={() => fillFromSuggestion(entry)}
                  />
                ))}
              </div>
            ) : null}
          </div>
        </div>

        <div className="stack">
          {recentVisitors.length > 0 ? (
            <div className="panel">
              <div className="panel-title-row">
                <div>
                  <h2 className="panel-title">Recent visits</h2>
                  <p className="panel-caption">Saved in this browser</p>
                </div>
              </div>

              <div className="list">
                {recentVisitors.map((visitor) => (
                  <div className="list-item" key={visitor.id}>
                    <div>
                      <strong>{visitor.visitorName}</strong>
                      <span>
                        {visitor.visitorPhone} · {visitor.staffName}
                      </span>
                      <p>{visitor.services.join(' · ')}</p>
                    </div>
                    <div className="item-meta">
                      <span className="badge soft bottom-stack-sm">
                        {visitor.feedbackDeliveryStatus === 'failed' ? 'Failed' : 'Sent'}
                      </span>
                      <strong>{formatInrCurrency(visitor.totalAmount)}</strong>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          <div className="panel">
            <div className="section-heading">
              <div>
                <h2>Selected staff</h2>
                <p>Who will own this visit when it is saved.</p>
              </div>
            </div>

            {selectedStaff ? (
              <div className="list-item">
                <div className="media-row lg">
                  <div className="avatar tinted" style={tintStyle(selectedStaff.color)}>
                    {getInitials(selectedStaff.name)}
                  </div>
                  <div>
                    <strong>{selectedStaff.name}</strong>
                    <span>{selectedStaff.role}</span>
                    <p>{selectedStaff.specialty}</p>
                  </div>
                </div>
                <strong>{formatInrCurrency(selectedStaff.revenue)}</strong>
              </div>
            ) : (
              <div className="empty-state">Choose a staff member to assign the visit.</div>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}
